import React, { Component } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { connect } from "react-redux";
import agent from "../../agent/agent";
import { VisibleContent } from "../../components";
import { RouterContext } from "../../layouts/CabinetLayout";
import { Filter, Table, TaskDrawer } from "./components";
import {
  TASK_REPORT_INIT_FILTER,
  TASK_REPORT_PROBE_LIMIT,
  buildTaskReportQuery,
  canReuseLatestDailyProbe,
  fetchContractTaskPeriods,
  fetchTaskDetailsById,
  filterTaskReportItemsByStatus,
  getLatestDailyDate,
  mergeTaskMetaWithDetails,
  normalizeTaskReportResponse,
  paginateItems,
  resolveTaskPeriods,
  toDailyDateParam,
} from "./helpers";

class Tasks extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      items: [],
      filter: { ...TASK_REPORT_INIT_FILTER },
      taskMeta: null,
      contractPeriods: [],
      periods: [],
      errorMessage: "",
      totalPages: 1,
      totalCount: 0,
      isLoading: true,
      isContractReload: false,
      contractId: null,
      hasUserSelectedDate: false,
      drawerOpen: false,
      drawerItem: null,
    };
  }

  componentDidMount = async () => {
    await this.initState();
  };

  componentDidUpdate = async (prevProps) => {
    const prevId = prevProps.contract?.available_contracts?.contract_id;
    const currentId = this.props.contract?.available_contracts?.contract_id;
    if (prevId !== currentId) {
      await this.reloadForContract();
    }
  };

  getActiveContractId = () =>
    this.props?.contract?.available_contracts?.contract_id || null;

  reloadForContract = async () => {
    await this.setState({
      isLoading: true,
      isContractReload: true,
      items: [],
      taskMeta: null,
      contractPeriods: [],
      periods: [],
      errorMessage: "",
      totalPages: 1,
      totalCount: 0,
      filter: { ...TASK_REPORT_INIT_FILTER },
      hasUserSelectedDate: false,
      drawerOpen: false,
      drawerItem: null,
    });
    window.history.replaceState(null, "", window.location.pathname);
    await this.initState({ resetFilter: true });
  };

  initState = async ({ resetFilter = false } = {}) => {
    const contractId = this.getActiveContractId();
    await this.setState({ contractId });

    const contractPeriods = await fetchContractTaskPeriods(contractId);
    if (this.getActiveContractId() !== contractId) return;

    await this.setState({ contractPeriods, periods: contractPeriods });
    await this.initFilter(resetFilter);
  };

  initFilter = async (resetFilter = false) => {
    const parsedFilter = { ...TASK_REPORT_INIT_FILTER };
    let hasUserSelectedDate = false;

    if (!resetFilter) {
      const search = new URLSearchParams(window.location.search);

      for (const [key, value] of search.entries()) {
        if (key === "daily_date") {
          parsedFilter.daily_date = value || null;
          hasUserSelectedDate = Boolean(value);
        } else if (key === "status") {
          parsedFilter.status = [...parsedFilter.status, value].filter(Boolean);
        } else if (key === "page" || key === "limit") {
          const number = parseInt(value, 10);
          if (!Number.isNaN(number)) parsedFilter[key] = number;
        }
      }
    }

    await this.setState({ filter: parsedFilter, hasUserSelectedDate }, async () => {
      await this.getItems();
    });
  };

  getItems = async () => {
    const { contractId, filter: stateFilter, hasUserSelectedDate } = this.state;
    let filter = stateFilter;
    const requestContractId = contractId;
    const hasStatusFilter = (filter.status || []).length > 0;
    const needsLatestDailyResolve = !hasUserSelectedDate && !filter.daily_date;

    await this.setState({ isLoading: true, errorMessage: "" });

    const queryFilter = {
      ...filter,
      status: [],
      daily_date: toDailyDateParam(filter.daily_date),
      // при фильтре статуса / поиске последней daily забираем весь день и режем на клиенте
      page: hasStatusFilter || needsLatestDailyResolve ? 1 : filter.page,
      limit: hasStatusFilter || needsLatestDailyResolve ? TASK_REPORT_PROBE_LIMIT : filter.limit,
    };
    const query = buildTaskReportQuery(queryFilter, contractId);
    const response = await agent
      .get(`/tasks/report?${query}`)
      .then((res) => res)
      .catch((err) => ({ error: err?.response }));

    if (response?.error) {
      if (this.getActiveContractId() !== requestContractId) return;
      await this.setState({
        isLoading: false,
        isContractReload: false,
        errorMessage: response?.error?.data?.details || "Не удалось загрузить сверку заданий",
        items: [],
        totalCount: 0,
        totalPages: 1,
        taskMeta: null,
      });
      return;
    }

    const { items, meta, total } = normalizeTaskReportResponse(response);

    if (this.getActiveContractId() !== requestContractId) return;

    if (needsLatestDailyResolve) {
      const latestDailyDate = toDailyDateParam(getLatestDailyDate(items) || meta?.daily_date);
      if (latestDailyDate) {
        filter = { ...filter, daily_date: latestDailyDate, page: 1 };
        await this.setState({
          filter,
          hasUserSelectedDate: true,
        });

        // Обычно API без даты уже отдаёт один день — второй запрос не нужен.
        // Повтор только если в ответе разные даты или день обрезан лимитом пробы.
        if (!canReuseLatestDailyProbe(items, latestDailyDate, total)) {
          await this.getItems();
          return;
        }
      }
    }

    const taskDetails = meta?.task_id ? await fetchTaskDetailsById(meta.task_id) : null;
    if (this.getActiveContractId() !== requestContractId) return;

    const taskMeta = mergeTaskMetaWithDetails(meta, taskDetails);
    const periods = resolveTaskPeriods({
      metaPeriods: meta?.periods,
      contractPeriods: this.state.contractPeriods,
      taskMeta,
    });

    const nextFilter = { ...filter };
    const filteredItems = filterTaskReportItemsByStatus(items, nextFilter.status);
    // Проба с limit=1000: после resolve даты режем пагинацию на клиенте
    const useClientPaging = hasStatusFilter || (
      needsLatestDailyResolve && Boolean(filter.daily_date)
    );
    const totalCount = useClientPaging ? filteredItems.length : total;
    const limit = nextFilter.limit || 15;
    const totalPages = Math.ceil(totalCount / limit) || 1;
    const page = Math.min(nextFilter.page || 1, totalPages);
    nextFilter.page = page;
    const visibleItems = useClientPaging
      ? paginateItems(filteredItems, page, limit)
      : filteredItems;

    await this.setState({
      isLoading: false,
      isContractReload: false,
      items: visibleItems,
      taskMeta,
      periods,
      totalCount,
      totalPages,
      filter: nextFilter,
      errorMessage: visibleItems.length
        ? ""
        : (hasStatusFilter ? "Нет записей для выбранного статуса выполнения" : "Нет данных"),
    });
    this.updateUrlSearch();
  };

  updateUrlSearch = () => {
    const { filter, hasUserSelectedDate } = this.state;
    const params = new URLSearchParams();

    if (hasUserSelectedDate && filter.daily_date) {
      params.set("daily_date", filter.daily_date);
    }
    (filter.status || []).forEach((status) => params.append("status", status));
    if (filter.page && filter.page !== 1) params.set("page", String(filter.page));
    if (filter.limit && filter.limit !== TASK_REPORT_INIT_FILTER.limit) {
      params.set("limit", String(filter.limit));
    }

    const search = params.toString();
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}`;
    window.history.replaceState(null, "", nextUrl);
  };

  changeFilter = async (filter, isFastState = false) => {
    await this.setState({
      filter,
      hasUserSelectedDate: Boolean(filter.daily_date),
    });
    if (!isFastState) return;
    await this.getItems();
  };

  handleOpenDrawer = (item) => {
    if (!item) return;
    this.setState({ drawerOpen: true, drawerItem: item });
  };

  handleCloseDrawer = () => {
    this.setState({ drawerOpen: false, drawerItem: null });
  };

  render() {
    const {
      items,
      filter,
      taskMeta,
      periods,
      errorMessage,
      totalPages,
      totalCount,
      isLoading,
      isContractReload,
      drawerOpen,
      drawerItem,
    } = this.state;

    return (
      <>
        <Box sx={{ paddingBottom: "40px" }}>
          <VisibleContent visible={isContractReload}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
              <CircularProgress />
            </Box>
          </VisibleContent>

          <VisibleContent visible={!isContractReload}>
            <Filter
              filter={filter}
              taskMeta={taskMeta}
              periods={periods}
              onChange={this.changeFilter}
            />

            <Box mt={2.5} />

            <VisibleContent visible={Boolean(errorMessage) && !isLoading && !items.length}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            </VisibleContent>

            <VisibleContent visible={!errorMessage || items.length > 0 || isLoading}>
              <Table
                items={items}
                filter={filter}
                totalPages={totalPages}
                totalCount={totalCount}
                isLoading={isLoading}
                onChangeFilter={this.changeFilter}
                onRowClick={this.handleOpenDrawer}
                selectedItemId={
                  drawerOpen && drawerItem
                    ? (drawerItem.id || `${drawerItem.task_row_id}-${drawerItem.status}`)
                    : null
                }
              />
            </VisibleContent>
          </VisibleContent>
        </Box>

        <TaskDrawer open={drawerOpen} item={drawerItem} onClose={this.handleCloseDrawer} />
      </>
    );
  }
}

export default connect((state) => ({
  contract: state.contracts?.contract || null,
}))(Tasks);

